// src/services/cacheBus.js
//
// Single fan-out point for cache invalidation across the data layer.
// Today the bus only knows about two stores — locationCache and
// reportsCache — but the rule of thumb is the same as the legacy
// monolith: any successful write to a portfolio entity busts every
// derived view at once.
//
// Why a separate module:
//   - Keeps locationsClient.js and leasesClient.js from importing
//     reportsCache directly. Reports are a downstream concern; the
//     write paths shouldn't even know they exist.
//   - Avoids circular deps between locationCache and reportsCache.
//   - One place to add new caches when slice 8/9 lands (lease summary
//     report, project status report, etc.). Add the import here.
//
// Stays sync. Cache invalidation is a pointer flip, not a network call.

import { invalidateAll as invalidateLocationCache }          from "./locationCache.js";
import { invalidateAll as invalidateReportsCache }           from "./reportsCache.js";
import { invalidateAll as invalidateLeasesPortfolioCache }   from "./leasesPortfolioCache.js";
import { invalidateAll as invalidateIntegrationHealthCache } from "./integrationHealthCache.js";
import { invalidateAll as invalidatePulseCache } from "./pulseCache.js";

// ─── Plan-invalidation subscribers ──────────────────────────────────────────
// Lightweight publish-subscribe for the "location data changed" signal.
//
// Why: /api/plan has no server-side cache — a fresh call always returns
// up-to-date data. The only reason MarketPlanPage ever showed stale names,
// capacity, or stage values was a missing re-fetch trigger: nothing told the
// page that a location write had happened while it was visible.
//
// How: every location write in locationsClient.js (alias, capacity, stage,
// headcount, disposition, photo, …) already calls invalidatePortfolioCaches()
// after the server confirms the row. We fan out from there to any registered
// subscriber so MarketPlanPage can fire a fresh getPlan() round-trip within
// ~1 second — no polling, no hard reload.
//
// Usage:
//   subscribePlanInvalidation(fn)   — add fn to the subscriber set (sync)
//   unsubscribePlanInvalidation(fn) — remove fn from the set (sync)
//
// Subscriber errors are caught so they never abort the cache-clearing work.
/** @type {Set<() => void>} */
const planInvalidationSubscribers = new Set();

export function subscribePlanInvalidation(fn) {
  planInvalidationSubscribers.add(fn);
}

export function unsubscribePlanInvalidation(fn) {
  planInvalidationSubscribers.delete(fn);
}

/**
 * Drop every cached portfolio aggregate. Called by every client write
 * (locationsClient, leasesClient) immediately after the server confirms
 * the row, before the audit envelope is queued.
 *
 * Also notifies plan-invalidation subscribers (e.g. MarketPlanPage) so they
 * can re-fetch /api/plan and reflect the new location data without a page
 * reload. Subscriber routes that trigger this:
 *   addLocation, updateLocation, deleteLocation, updateHealth, updateStage,
 *   uploadLocationPhoto, deleteLocationPhoto, addCapacityScheduleRow,
 *   removeCapacityScheduleRow, editCapacityScheduleRow,
 *   addHeadcountScheduleRow, removeHeadcountScheduleRow
 *   (all in src/data/locationsClient.js)
 */
export function invalidatePortfolioCaches() {
  invalidateLocationCache();
  invalidateReportsCache();
  invalidateLeasesPortfolioCache();
  invalidateIntegrationHealthCache();
  for (const fn of planInvalidationSubscribers) {
    try { fn(); } catch (_) { /* subscriber errors must not abort cache clearing */ }
  }
}

// Attendance source is a server-selected analytical contract. The admin
// source toggle clears both stores and tells mounted analytical surfaces to
// fetch again instead of continuing to display the previous mode.
const attendanceSourceSubscribers = new Set();

export function subscribeAttendanceSourceChange(fn) {
  attendanceSourceSubscribers.add(fn);
  return () => attendanceSourceSubscribers.delete(fn);
}

export function emitAttendanceSourceChange() {
  invalidatePortfolioCaches();
  for (const fn of attendanceSourceSubscribers) {
    try { fn(); } catch (_) { /* one mounted view must not block another */ }
  }
}

// ─── Command-index-stale subscribers ────────────────────────────────────────
// Lightweight publish-subscribe for the "plan data changed, command index
// may be stale" signal.
//
// Why: GET /api/command-index has no server-side cache — it always runs live.
// The problem is the client (PortfolioCommandTile, PortfolioCommandPage) only
// fetches on mount. After a location plan save that changes disposition or
// targetActionDate, the Plan Coverage pillar score may shift, so we need to
// re-request the command index without a full page reload.
//
// Emitted by: MarketPlanPage.handleOfficeSaved when patch contains
//   `disposition` or `targetActionDate`, and by source clients after a
//   successful headcount/lease/capacity/AI write.
// Subscribed by: PortfolioCommandTile, PortfolioCommandPage.
//
// Subscriber errors are caught so they never abort the cache-clearing work.
/** @type {Set<() => void>} */
const commandIndexStaleSubscribers = new Set();

export function subscribeCommandIndexStale(fn) {
  commandIndexStaleSubscribers.add(fn);
}

export function unsubscribeCommandIndexStale(fn) {
  commandIndexStaleSubscribers.delete(fn);
}

export function emitCommandIndexStale() {
  for (const fn of commandIndexStaleSubscribers) {
    try { fn(); } catch (_) { /* subscriber errors must not abort cache clearing */ }
  }
}

// ─── Imagery-invalidation subscribers ────────────────────────────────────────
// Successful Image Library writes emit this signal after the server response
// succeeds. Consumers re-read their imagery assignments immediately; this is
// intentionally event-driven and has no TTL or polling fallback.
/** @type {Set<() => void>} */
const imageryInvalidationSubscribers = new Set();

export function subscribeImageryInvalidation(fn) {
  imageryInvalidationSubscribers.add(fn);
}

export function unsubscribeImageryInvalidation(fn) {
  imageryInvalidationSubscribers.delete(fn);
}

export function emitImageryInvalidation() {
  for (const fn of imageryInvalidationSubscribers) {
    try { fn(); } catch (_) { /* subscriber errors must not abort other listeners */ }
  }
}

/** Notify mounted FX consumers to reload the selected conversion basis. */
export function invalidateFxRates() {
  // Portfolio lease dollars are already FX-resolved. Invalidate even when Home
  // is unmounted, before any mounted financial consumer starts its new read.
  invalidateLeasesPortfolioCache();
  invalidatePulseCache();
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("ripple:fx-rates-invalidated"));
  }
}
